import React from "react";
import { CONTRACT_SECTIONS } from "./Variables/variables.config";

interface ContractPreviewViewProps {
  variables: Record<string, string>;
}

function renderContractText(templateParts: Array<string | { var: string }>, variables: Record<string, string>): string {
  return templateParts
    .map((part) => (typeof part === 'string' ? part : variables[part.var] || `[${part.var}]`))
    .join('');
}

export const ContractPreviewView: React.FC<ContractPreviewViewProps> = ({ variables }) => {
  return (
    <div className="p-4 md:py-10 md:pl-16 w-full min-h-screen">
      <div className="px-8 py-4 border border-white/10 rounded-lg">
        <div className="max-w-4xl mx-auto">
          {/* Contract Header */}
          <div className="text-center mb-12">
            <h1 className="text-3xl font-bold text-white mb-4">MUSIC PRODUCTION AGREEMENT</h1>
            <div className="h-px bg-white/20 my-6"></div>
          </div>

          {/* Contract Body */}
          <div className="space-y-8 text-neutral-300 leading-relaxed">
            {/* Introduction */}
            <div className="space-y-4">
              <p>
                This Agreement is entered into on [DATE] between [PRODUCER_NAME] ("Producer") 
                and [ARTIST_NAME] ("Artist") for the production of musical recordings.
              </p>
            </div>

            {/* Contract Sections */}
            {CONTRACT_SECTIONS.map((section, index) => (
              <div key={section.id} className="space-y-4">
                <h2 className="text-xl font-semibold text-white mb-3">
                  {index + 1}. {section.title}
                </h2>
                <p className="text-neutral-300 leading-7 pl-4">
                  {renderContractText(section.templateParts, variables)}
                </p>
              </div>
            ))}

            {/* Additional Standard Clauses */}
            <div className="space-y-6">
              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white mb-3">4. Intellectual Property</h2>
                <p className="text-neutral-300 leading-7 pl-4">
                  All rights to the musical compositions and sound recordings created under this agreement 
                  shall be jointly owned by Producer and Artist unless otherwise specified in writing.
                </p>
              </div>

              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white mb-3">5. Confidentiality</h2>
                <p className="text-neutral-300 leading-7 pl-4">
                  Both parties agree to maintain confidentiality regarding all creative materials, 
                  business terms, and proprietary information shared during the course of this agreement.
                </p>
              </div>

              <div className="space-y-4">
                <h2 className="text-xl font-semibold text-white mb-3">6. Governing Law</h2>
                <p className="text-neutral-300 leading-7 pl-4">
                  This agreement shall be governed by the laws of [STATE/JURISDICTION] and any disputes 
                  shall be resolved through binding arbitration.
                </p>
              </div>
            </div>

            {/* Signature Block */}
            <div className="mt-16 space-y-8">
              <div className="h-px bg-white/20"></div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-12">
                <div className="space-y-4">
                  <div className="text-white font-medium">PRODUCER:</div>
                  <div className="space-y-3">
                    <div className="h-px bg-white/30 w-48"></div>
                    <div className="text-sm text-neutral-400">[PRODUCER_NAME]</div>
                    <div className="text-sm text-neutral-400">Date: ___________</div>
                  </div>
                </div>
                <div className="space-y-4">
                  <div className="text-white font-medium">ARTIST:</div>
                  <div className="space-y-3">
                    <div className="h-px bg-white/30 w-48"></div>
                    <div className="text-sm text-neutral-400">[ARTIST_NAME]</div>
                    <div className="text-sm text-neutral-400">Date: ___________</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};
